/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { ActionButton, Dropdown, isEmptyValue, Modal, TextInput, LoadingSpinner } from '@blend/enterprise-components';
import { WrappedInput } from 'blb-components/WrappedInput';
import { ModalContent } from 'blb-components/ModalContent';
import { css, StyleSheet } from 'aphrodite';
import { FinalFormInputConfig } from 'blb-ui-types/helpers';
import { Field, Form } from 'react-final-form';
import { DocumentUploadStatus } from 'blb-components/AuthenticatedApp/DashboardPage/Docs';
import { Dispatch, SetStateAction } from 'react';
import { getPersona } from 'blb-persona/index';
import { MeContext } from 'blb-contexts/Me';

export interface UploadFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (fields: FormFields) => void;
  setUploadStatus: Dispatch<SetStateAction<DocumentUploadStatus>>;
  uploadFileName: string | undefined;
  uploadStatus: DocumentUploadStatus;
}

export interface FormFields {
  docName: string;
  docType: string;
}

const styles = StyleSheet.create({
  fieldWrapper: {
    alignItems: 'center',
    flexBasis: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  footerBar: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
    marginTop: 28,
  },
  loadingSpinnerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 10,
    padding: '40px 0',
  },
  row: {
    ':not(:last-child)': {
      marginBottom: 14,
    },
  },
  topSectionWrapper: {
    paddingBottom: 24,
  },
});

export type acceptedDocumentTypes =
  | 'APPRAISAL'
  | 'APPRAISAL_REPORT_2_-_4_UNIT'
  | 'APPRAISAL_REPORT_AVM'
  | 'APPRAISAL_REPORT_COOP'
  | 'APPRAISAL_REPORT_DESK_REVIEW'
  | 'APPRAISAL_REPORT_EXTERIOR_ONLY'
  | 'APPRAISAL_REPORT_MANUFACTURED_HOME'
  | 'APPRAISAL_REPORT_OPERATING_INCOME_SCHEDULE'
  | 'APPRAISAL_REPORT_RENT_SURVEY'
  | 'APPRAISAL_REPORT_SINGLE FAMILY'
  | 'APPRAISAL_REPORT_UPDATE_AND_OR_COMPLETION_REPORT'
  | 'APPRAISAL_REPORT_PROPERTY_CONDITION_REPORT'
  | 'APPRAISAL_REPORT_MARKET_CONDITIONS_ADDENDUM'
  | 'APPRAISAL_WAIVER'
  | 'CLOSING_DISCLOSURE'
  | 'CLOSING_PACKAGE'
  | 'CLOSING_PROTECTION_LETTER'
  | 'DEED_OF_TRUST'
  | 'HOA_BILL'
  | 'SETTLEMENT_STATEMENT_HUD1'
  | 'MORTGAGE_STATEMENT'
  | 'PAYOFF_STATEMENT'
  | 'PRELIMINARY_CLOSING_DOCUMENTS'
  | 'PROPERTY_TAX_BILL'
  | 'SIGNED_CLOSING_DOCUMENTS'
  | 'TAX_CERTIFICATE'
  | 'TITLE:_CHAIN_OF_TITLE'
  | 'TITLE:_CLOSING_PROTECTION_LETTER'
  | 'TITLE:_ERRORS_AND_OMISSIONS'
  | 'TITLE:_LIEN_JUDGMENT_SUPPORTING_DOC'
  | 'SATISFACTION_OF_SECURITY_INSTRUMENT_LIEN_RELEASE'
  | 'TITLE:OTHER'
  | 'TITLE:_TAX_SUPPORTING_DOC'
  | 'TITLE:_WIRING_INSTRUCTIONS'
  | 'TITLE_DOCUMENT'
  | 'BORROWER_AUTHORIZATION'
  | 'TRAILING_DOCUMENTS'
  | 'INVOICE_UTILITY_BILL';

export const supportedDocumentTypeDropdownOptions: { text: string; value: acceptedDocumentTypes }[] = [
  { text: 'Appraisal', value: 'APPRAISAL' },
  { text: 'Appraisal Report - 2-4 Unit', value: 'APPRAISAL_REPORT_2_-_4_UNIT' },
  { text: 'Appraisal Report - AVM - Desktop Appraisal', value: 'APPRAISAL_REPORT_AVM' },
  { text: 'Appraisal Report - Coop', value: 'APPRAISAL_REPORT_COOP' },
  { text: 'Appraisal Report - Desk Review', value: 'APPRAISAL_REPORT_DESK_REVIEW' },
  { text: 'Appraisal Report - Exterior Only (Drive-by)', value: 'APPRAISAL_REPORT_EXTERIOR_ONLY' },
  { text: 'Appraisal Report - Manufactured Home', value: 'APPRAISAL_REPORT_MANUFACTURED_HOME' },
  { text: 'Appraisal Report - Operating Income Schedule', value: 'APPRAISAL_REPORT_OPERATING_INCOME_SCHEDULE' },
  { text: 'Appraisal Report - Rent Survey', value: 'APPRAISAL_REPORT_RENT_SURVEY' },
  { text: 'Appraisal Report - Single Family', value: 'APPRAISAL_REPORT_SINGLE FAMILY' },
  {
    text: 'Appraisal Report - Update and/or Completion Report',
    value: 'APPRAISAL_REPORT_UPDATE_AND_OR_COMPLETION_REPORT',
  },
  { text: 'Appraisal Report - Property Condition Report', value: 'APPRAISAL_REPORT_PROPERTY_CONDITION_REPORT' },
  { text: 'Appraisal Report - Market Conditions Addendum', value: 'APPRAISAL_REPORT_MARKET_CONDITIONS_ADDENDUM' },
  { text: 'Appraisal Waiver', value: 'APPRAISAL_WAIVER' },
  { text: 'Closing Disclosure (CD)', value: 'CLOSING_DISCLOSURE' },
  { text: 'Closing Package', value: 'CLOSING_PACKAGE' },
  { text: 'Closing Protection Letter (CPL)', value: 'CLOSING_PROTECTION_LETTER' },
  { text: 'Deed Of Trust', value: 'DEED_OF_TRUST' },
  { text: "Homeowner's Association Bill", value: 'HOA_BILL' },
  { text: 'HUD-1: Settlement Statement', value: 'SETTLEMENT_STATEMENT_HUD1' },
  { text: 'Mortgage Statement', value: 'MORTGAGE_STATEMENT' },
  { text: 'Payoff Statement', value: 'PAYOFF_STATEMENT' },
  { text: 'Preliminary Closing Documents', value: 'PRELIMINARY_CLOSING_DOCUMENTS' },
  { text: 'Property Tax Bill', value: 'PROPERTY_TAX_BILL' },
  { text: 'Signed Closing Documents', value: 'SIGNED_CLOSING_DOCUMENTS' },
  { text: 'Tax Certificate', value: 'TAX_CERTIFICATE' },
  { text: 'Title - 24-Month Chain of Title', value: 'TITLE:_CHAIN_OF_TITLE' },
  { text: 'Title - Closing Protection Letter (CPL)', value: 'TITLE:_CLOSING_PROTECTION_LETTER' },
  { text: 'Title - Errors & Omissions (E&O)', value: 'TITLE:_ERRORS_AND_OMISSIONS' },
  { text: 'Title - Lien / Judgment Supporting Document', value: 'TITLE:_LIEN_JUDGMENT_SUPPORTING_DOC' },
  { text: 'Title - Lien Release', value: 'SATISFACTION_OF_SECURITY_INSTRUMENT_LIEN_RELEASE' },
  { text: 'Title - Other', value: 'TITLE:OTHER' },
  { text: 'Title - Tax Supporting Document', value: 'TITLE:_TAX_SUPPORTING_DOC' },
  { text: 'Title - Wiring Instructions', value: 'TITLE:_WIRING_INSTRUCTIONS' },
  { text: 'Title Document', value: 'TITLE_DOCUMENT' },
  { text: 'Trailing Documents', value: 'TRAILING_DOCUMENTS' },
  { text: 'Utility Bill', value: 'INVOICE_UTILITY_BILL' },
];

const documentTypeInputConfig: FinalFormInputConfig<Dropdown.Props, string> = {
  component: WrappedInput,
  customComponentProps: { options: supportedDocumentTypeDropdownOptions },
  inputComponent: Dropdown,
  label: 'Document type',
  labelSize: 'sm',
  name: 'docType',
  validate: isEmptyValue('Please provide a document type'),
};

const documentNameInputConfig: FinalFormInputConfig<Dropdown.Props, string> = {
  component: WrappedInput,
  customComponentProps: { options: supportedDocumentTypeDropdownOptions },
  disabled: true,
  inputComponent: TextInput,
  label: 'File name',
  labelSize: 'sm',
  name: 'docName',
  value: '',
};

export const UploadFileModal: React.FC<UploadFileModalProps> = props => {
  const me = React.useContext(MeContext)!;
  const persona = getPersona(me.principalType);
  const supportInfo = persona.getSupportContactInfo();
  const renderModalContent = () => {
    if (props.uploadStatus === 'UPLOADING') {
      return (
        <div className={css(styles.loadingSpinnerWrapper)} id="it-FileUploadModal-uploading">
          <LoadingSpinner size="lg" theme="secondary" />
        </div>
      );
    } else if (props.uploadStatus === 'ERROR') {
      return (
        <div id="it-FileUploadModal-error">
          <div>Looks like we hit a snag uploading. Please try again, or contact {supportInfo.email}</div>
          <div className={css(styles.footerBar)}>
            <ActionButton id="NewFileUpload-cancel" label="Cancel" onClick={props.onClose} theme="tertiary" />
            <ActionButton
              id="NewFileUpload-re-upload"
              label="Re-upload"
              onClick={() => {
                props.setUploadStatus('DEFAULT');
              }}
              type="submit"
            />
          </div>
        </div>
      );
    }
    return (
      <Form initialValues={{ docName: props.uploadFileName }} onSubmit={props.onSubmit}>
        {({ handleSubmit }) => {
          return (
            <form id="it-FileUploadModal-form" onSubmit={handleSubmit}>
              <div className={css(styles.topSectionWrapper)}>
                <div className={css(styles.row)} key={`NewFileUpload_docName`}>
                  <div className={css(styles.fieldWrapper)}>
                    <Field {...documentNameInputConfig} />
                  </div>
                </div>
                <div className={css(styles.row)} key={`NewFileUpload_docType`}>
                  <div className={css(styles.fieldWrapper)}>
                    <Field {...documentTypeInputConfig} />
                  </div>
                </div>
              </div>
              <div className={css(styles.footerBar)}>
                <ActionButton id="NewFileUpload-cancel" label="Cancel" onClick={props.onClose} theme="tertiary" />
                <ActionButton id="NewFileUpload-upload" label="Upload" type="submit" />
              </div>
            </form>
          );
        }}
      </Form>
    );
  };

  const getModalTitle = () => {
    switch (props.uploadStatus) {
      case 'UPLOADING':
        return 'Uploading';
      case 'ERROR':
        return 'File upload failed';
      case 'DEFAULT':
      default:
        return 'Upload File';
    }
  };

  return (
    <Modal contentLabel={getModalTitle()} handleClose={props.onClose} isOpen={props.isOpen}>
      <ModalContent handleClose={props.onClose} headerText={getModalTitle()}>
        {renderModalContent()}
      </ModalContent>
    </Modal>
  );
};
